import { Button } from '@avangard/ui/core';

import testPlaceholderFailPath from '@modules/testing/helpers/assets/test-fail.svg';
import testPlaceholderSuccessPath from '@modules/testing/helpers/assets/test-completed.svg';

import { CommonHelper } from '@helpers';
import { TestingResults } from '@modules/testing/organisms';

import type { CourseEntity } from '@modules/courses/entities';
import type { SubjectEntity } from '@modules/subjects/main/entities';
import type { TestEntity } from '@modules/testing/entities';

export type TestingPlaceholderPayload = {
    course: CourseEntity;
    subject: SubjectEntity;
    t: (key: string, options?: Record<string, any>) => string;
    test?: TestEntity;
    loading?: boolean;
    onStart?: () => void | Promise<void>;
    onClick?: () => void | Promise<void>;
    onCancel?: () => void;
};

export type TestingPlaceholderDataProps = {
    type: 'default' | 'success' | 'failure' | 'successWithResult' | 'failureWithResult';
    payload: TestingPlaceholderPayload;
};

const imagesByTypeMap = {
    default: testPlaceholderSuccessPath,
    success: testPlaceholderSuccessPath,
    failure: testPlaceholderFailPath,
    successWithResult: testPlaceholderSuccessPath,
    failureWithResult: testPlaceholderFailPath,
};

const getImage = (type: TestingPlaceholderDataProps['type']) => {
    const imagePath = imagesByTypeMap[type];

    return <img src={imagePath} alt='testing-subject-placeholder' />;
};

const getContent = (
    type: TestingPlaceholderDataProps['type'],
    payload: TestingPlaceholderPayload,
): JSX.Element => {
    const { t, course, subject, test } = payload;

    const isCourseTest = !subject.exists();
    const isEventTest = course.passTestForAnyResult;

    switch (type) {
        case 'default':
            const testQuestionsCount = subject.exists()
                ? subject.test.questionsCount
                : course.test.questionsCount;

            const testDuration = subject.exists() ? subject.test.duration : course.test.duration;

            const count = CommonHelper.createCountFormatter(testQuestionsCount, [
                t('common:blocks.questions.one'),
                t('common:blocks.questions.some'),
                t('common:blocks.questions.multiple'),
            ]);

            const beforeDuration = CommonHelper.createCountFormatter(
                testDuration,
                [
                    t('common:blocks.before.one'),
                    t('common:blocks.before.some'),
                    t('common:blocks.before.multiple'),
                ],
                { withNumber: false },
            );

            const duration = CommonHelper.createCountFormatter(testDuration, [
                t('common:blocks.minutes.one'),
                t('common:blocks.minutes.some'),
                t('common:blocks.minutes.multiple'),
            ]);

            if (isEventTest) {
                return (
                    <>
                        <p
                            dangerouslySetInnerHTML={{
                                __html: t('testing:blocks.titles.will_pass_test_for_event', {
                                    event: course.name,
                                }),
                            }}
                        />
                        <p>
                            {t('testing:blocks.titles.will_pass_test_parts', {
                                questionsCount: count,
                                beforeDuration,
                                duration,
                            })}
                        </p>
                    </>
                );
            }

            if (!isCourseTest) {
                const scoreDescription = t('testing:blocks.titles.score_description', {
                    returnObjects: true,
                }) as unknown as string[];

                const scoreDescriptionHtml = scoreDescription
                    .map(item => `<p>${item}</p>`)
                    .join('');

                return (
                    <>
                        <p>
                            {t('testing:blocks.titles.will_pass_test_for_subject')}
                            <br />
                            <strong>«{subject.name}».</strong>

                            <br />
                            <br />

                            {t('testing:blocks.titles.will_pass_test_parts', {
                                questionsCount: count,
                                beforeDuration,
                                duration,
                            })}
                        </p>

                        <br />

                        <div dangerouslySetInnerHTML={{ __html: scoreDescriptionHtml }} />
                    </>
                );
            }

            const scoreDescription = t('testing:blocks.titles.score_description', {
                returnObjects: true,
            }) as unknown as string[];

            const scoreDescriptionHtml = scoreDescription.map(item => `<p>${item}</p>`).join('');

            return (
                <p>
                    {t('testing:blocks.titles.will_pass_test_for_course')}
                    <br />

                    {t('testing:blocks.titles.will_pass_test_parts', {
                        questionsCount: count,
                        beforeDuration,
                        duration,
                    })}
                    <br />
                    <br />

                    <div dangerouslySetInnerHTML={{ __html: scoreDescriptionHtml }} />
                    <br />

                    {t('testing:blocks.titles.final_assessment')}
                </p>
            );

        case 'successWithResult': {
            if (!test) {
                return <p>{t('testing:blocks.titles.congratulation')}</p>;
            }

            const { resultScore } = test.getScoresByType(course.test.showResultScore);

            return (
                <>
                    {getContent('success', payload)}

                    <TestingResults
                        userId={test.userId}
                        courseId={course.id}
                        subject={{
                            id: subject.id,
                            name: subject.name,
                            score: resultScore,
                        }}
                    />
                </>
            );
        }
        case 'success': {
            if (!test) {
                return <p>{t('testing:blocks.titles.congratulation')}</p>;
            }

            const { resultScore, maxScore } = test.getScoresByType(course.test.showResultScore);
            const score = CommonHelper.createCountFormatter(
                resultScore,
                [
                    t('common:blocks.points.one'),
                    t('common:blocks.points.some'),
                    t('common:blocks.points.multiple'),
                ],
                { decimal: 2 },
            );

            if (isEventTest) {
                return (
                    <>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: t('testing:blocks.titles.congratulation_with_name', {
                                    name: course.name,
                                }),
                            }}
                        />
                        <br />
                        <strong>
                            {t('testing:blocks.titles.your_scores', {
                                score,
                                maxScore,
                            })}
                        </strong>
                    </>
                );
            }

            return (
                <p>
                    {t('testing:blocks.titles.congratulation')}
                    <br />

                    {t('testing:blocks.titles.your_scores', { score, maxScore })}
                    <br />

                    {isCourseTest ? (
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t('testing:blocks.titles.your_course_usual_score', {
                                    usualScore: test.usualScore,
                                }),
                            }}
                        />
                    ) : (
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t('testing:blocks.titles.your_usual_score', {
                                    usualScore: test.usualScore,
                                }),
                            }}
                        />
                    )}

                    <br />
                    {isCourseTest ? (
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t('testing:blocks.titles.your_course_final_assessment', {
                                    score: test.finalAssessmentScore,
                                }),
                            }}
                        />
                    ) : null}
                </p>
            );
        }

        case 'failure': {
            if (!test) {
                return <p>{t('testing:blocks.titles.min_scores')}</p>;
            }

            const { resultScore, maxScore } = test.getScoresByType(course.test.showResultScore);
            const score = CommonHelper.createCountFormatter(
                resultScore,
                [
                    t('common:blocks.points.one'),
                    t('common:blocks.points.some'),
                    t('common:blocks.points.multiple'),
                ],
                { decimal: 2 },
            );

            return (
                <p>
                    {t('testing:blocks.titles.your_scores', { score, maxScore })}
                    <br />
                    {t('testing:blocks.titles.min_scores')}
                </p>
            );
        }
        case 'failureWithResult':
            if (!test) {
                return <p>{t('testing:blocks.titles.min_scores')}</p>;
            }

            const { resultScore } = test.getScoresByType(course.test.showResultScore);

            return (
                <>
                    {getContent('failure', payload)}

                    <TestingResults
                        userId={test.userId}
                        courseId={course.id}
                        subject={{
                            id: subject.id,
                            name: subject.name,
                            score: resultScore,
                        }}
                    />
                </>
            );
    }
};

const getActions = (
    type: TestingPlaceholderDataProps['type'],
    payload: TestingPlaceholderPayload,
) => {
    const { t, loading, onStart, onCancel, onClick, course } = payload;

    switch (type) {
        case 'default':
            return (
                <Button loading={loading} onClick={onStart}>
                    {t('testing:actions.start_test')}
                </Button>
            );
        case 'successWithResult':
        case 'success':
            if (course.passTestForAnyResult) {
                return (
                    <>
                        {!!onClick && (
                            <Button onClick={onClick}>
                                {t('testing:actions.download_certificate')}
                            </Button>
                        )}
                    </>
                );
            }

            return (
                <>{!!onCancel && <Button onClick={onCancel}>{t('common:actions.close')}</Button>}</>
            );

        case 'failureWithResult':
        case 'failure':
            return (
                <>
                    <Button loading={loading} onClick={onStart}>
                        {t('testing:actions.try_again')}
                    </Button>
                    {!!onCancel && (
                        <Button variant='outlined' disabled={loading} onClick={onCancel}>
                            {t('testing:actions.prepare')}
                        </Button>
                    )}
                </>
            );
    }
};

const getTestingPlaceholderData = (
    type: TestingPlaceholderDataProps['type'],
    payload: TestingPlaceholderPayload,
) => {
    const result = {
        image: getImage(type),
        content: getContent(type, payload),
        actions: getActions(type, payload),
    };

    return result;
};

export { getTestingPlaceholderData };
